// The handouts section of a session file (v8.363.10): metadata, notes and
// links. Files are NOT carried - they die with the session - so an image or
// a PDF comes back as a card that says so. Pure.
import { emptyHandout, type Handout, type HandoutKind } from './types';

export interface HandoutFileEntry {
  kind: HandoutKind;
  title: string;
  folder: string;
  tags: string[];
  notes: string;
  url: string;
  /** False for an image or a PDF: the bytes stayed behind. */
  carried: boolean;
}

const KINDS: HandoutKind[] = ['image', 'pdf', 'note', 'link'];

export function handoutsToFile(handouts: readonly Handout[]): HandoutFileEntry[] {
  return handouts.map((h) => ({
    kind: h.kind,
    title: h.title,
    folder: h.folder,
    tags: [...h.tags],
    notes: h.notes,
    url: h.url,
    carried: h.kind === 'note' || h.kind === 'link',
  }));
}

/** Tolerant: a row that is not a handout is dropped, a field that is not a string is blanked. */
export function handoutsFromFile(raw: unknown): HandoutFileEntry[] {
  if (!Array.isArray(raw)) return [];
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return raw
    .filter(
      (r): r is Record<string, unknown> =>
        !!r && typeof r === 'object' && KINDS.includes((r as { kind?: unknown }).kind as HandoutKind),
    )
    .map((r) => {
      const kind = r.kind as HandoutKind;
      return {
        kind,
        title: str(r.title),
        folder: str(r.folder),
        tags: Array.isArray(r.tags) ? r.tags.filter((t): t is string => typeof t === 'string') : [],
        notes: str(r.notes),
        url: str(r.url),
        carried: kind === 'note' || kind === 'link',
      };
    });
}

/** A draft from a file entry - no file, no audience, the GM's to reveal. */
export function handoutFromEntry(e: HandoutFileEntry, id: string, by: string, now: number): Handout {
  return {
    ...emptyHandout(id, e.kind, by, now),
    title: e.title,
    folder: e.folder,
    tags: [...new Set(e.tags)],
    notes: e.notes,
    url: e.url,
    size: e.kind === 'note' ? e.notes.length : 0,
  };
}
